/** @jsxImportSource @emotion/react */
import React from 'react';
import styled from '@emotion/styled';
import * as color from '@constant/color';
import { IAdminVocabularyBookData } from '@datatype/admin/api-type';
import VocabularyBookItem from '@component/admin/vocabulary-book/vocabulary-book-home/vocabulary-book-list/vocabulary-book-item';
import Spinner from '@design-system/spinner';

interface IProps {
  books: IAdminVocabularyBookData[];
  selectedBookKey: string | null;
  isLoading: boolean;
  isFetchingNextPage: boolean;
  isError: boolean;
  handleSelect: (book: IAdminVocabularyBookData) => void;
  handleRetry: () => void;
  sentinelRef: React.RefObject<HTMLDivElement | null>;
}

const List = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 8px;
`;

const Loader = styled.div`
  display: flex;
  justify-content: center;
  padding: 40px 0;
`;

const NextLoader = styled.div`
  display: flex;
  justify-content: center;
  padding: 16px 0;
`;

const Empty = styled.div`
  padding: 40px 0;
  text-align: center;
  color: ${color.BLACK_5};
`;

const Sentinel = styled.div`
  height: 1px;
`;

const VocabularyBookList: React.FC<IProps> = ({
  books,
  selectedBookKey,
  isLoading,
  isFetchingNextPage,
  isError,
  handleSelect,
  handleRetry,
  sentinelRef,
}) => {
  if (isLoading) {
    return (
      <Loader>
        <Spinner size='32px' color={color.BROWN_1} />
      </Loader>
    );
  }
  if (isError) {
    return (
      <Empty>
        데이터를 불러오지 못했습니다.{' '}
        <span
          style={{ color: color.BROWN_1, cursor: 'pointer', textDecoration: 'underline' }}
          onClick={handleRetry}>
          다시 시도
        </span>
      </Empty>
    );
  }
  if (books.length === 0) {
    return <Empty>조건에 맞는 단어집이 없습니다.</Empty>;
  }
  return (
    <>
      <List>
        {books.map((book) => (
          <VocabularyBookItem
            key={book.key}
            book={book}
            isSelected={selectedBookKey === book.key}
            handleClick={() => handleSelect(book)}
          />
        ))}
      </List>
      {isFetchingNextPage && (
        <NextLoader>
          <Spinner size='24px' color={color.BROWN_1} />
        </NextLoader>
      )}
      <Sentinel ref={sentinelRef} />
    </>
  );
};

export default VocabularyBookList;